import React, { useState } from 'react';
import { Shield, AlertTriangle, Check, MessageSquare, ExternalLink, Bell, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api } from '../api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import EmptyState from '../components/EmptyState';

const AdminModeration = () => {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [selectedReport, setSelectedReport] = useState(null);
  const [responseText, setResponseText] = useState('');

  const { data: reportsData, isLoading } = useQuery({
    queryKey: ['admin', 'reports'],
    queryFn: () => api.get('/reports')
  });

  const reports = reportsData?.data || [];
  const pendingCount = reports.filter(r => r.status === 'pending').length;
  const resolvedCount = reports.filter(r => r.status === 'resolved').length;

  const resolveMutation = useMutation({
    mutationFn: ({ id, action, note }) => {
      const endpoint = action === 'resolve' ? 'resolve' : (action === 'warn' ? 'warn' : 'ban');
      return api.post(`/reports/${id}/${endpoint}`, { note });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['admin', 'reports']);
      addToast(`Report #${variables.id} action completed: ${variables.action}`, 'success');
      setSelectedReport(null);
      setResponseText('');
    }
  });

  const handleAction = (id, action) => {
    resolveMutation.mutate({ id, action, note: responseText });
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-10 text-center md:text-left">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Moderation Center</h1>
        <p className="text-gray-500 font-medium mt-1">Review flagged content and maintain platform integrity.</p>
      </div>

      <div className="bg-white rounded-[2.5rem] border border-gray-100 overflow-hidden shadow-sm">
        <div className="p-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-xl font-bold text-sm">
              <AlertTriangle size={18} />
              {pendingCount} Pending Reports
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-xl font-bold text-sm">
              <Check size={18} />
              {resolvedCount} Resolved
            </div>
          </div>
          <button className="text-sm font-bold text-brand-indigo uppercase tracking-widest hover:underline">Moderation History</button>
        </div>

        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-gray-50">
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-widest">Incident</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-widest">Type</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-widest">Priority</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-widest">Status</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {isLoading ? (
              <tr>
                <td colSpan="5" className="px-8 py-20 text-center text-gray-400">
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="animate-spin" size={40} />
                    <p className="font-bold">Loading reports...</p>
                  </div>
                </td>
              </tr>
            ) : reports.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-8 py-20">
                  <EmptyState 
                    icon={Check}
                    variant="green"
                    title="All clear!"
                    description="No pending reports are currently awaiting your review."
                  />
                </td>
              </tr>
            ) : reports.map((report) => (
              <React.Fragment key={report.id}>
                <tr className={cn(
                  "hover:bg-gray-50/30 transition-colors group",
                  report.status !== 'pending' && "opacity-60"
                )}>
                  <td className="px-8 py-5">
                    <div>
                      <p className="font-bold text-gray-900 text-sm">
                        {report.reportable?.name || report.reportable?.title || report.reportable?.text || `ID: ${report.reportable_id}`}
                      </p>
                      <p className="text-xs text-gray-400 font-medium">
                        Reported by {report.reporter?.name || 'Unknown'} • {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <span className="text-sm font-bold text-gray-700">{report.reportable_type.split('\\').pop()}</span>
                  </td>
                  <td className="px-8 py-5">
                    <span className={cn(
                      "text-xxs font-bold px-2 py-1 rounded-lg uppercase tracking-widest",
                      report.reason.toLowerCase().includes('critical') || report.reason.toLowerCase().includes('abuse') 
                        ? "text-red-700 bg-red-100" 
                        : "text-gray-400 bg-gray-50"
                    )}>
                      {report.reason.split(' ')[0]}
                    </span>
                  </td>
                  <td className="px-8 py-5">
                    <span className={cn(
                      "text-xs font-bold",
                      report.status === 'pending' ? "text-orange-600" : "text-green-600"
                    )}>
                      {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setSelectedReport(selectedReport === report.id ? null : report.id)}
                        disabled={report.status !== 'pending'}
                        className={cn(
                          "p-2 rounded-xl transition-all",
                          selectedReport === report.id ? "bg-brand-indigo text-white" : "bg-gray-50 text-gray-400 hover:text-brand-indigo hover:bg-brand-indigo/5",
                          report.status !== 'pending' && "cursor-not-allowed grayscale"
                        )}
                        title="Respond/Action"
                      >
                        <MessageSquare size={18} />
                      </button>
                      <Link to={`/admin/activities?id=${report.reportable_id}`} className="p-2 bg-brand-indigo/10 text-brand-indigo rounded-xl hover:bg-brand-indigo/20 transition-all" title="Review Activity">
                        <ExternalLink size={18} />
                      </Link>
                    </div>
                  </td>
                </tr>
                {selectedReport === report.id && (
                  <tr className="bg-gray-50/50">
                    <td colSpan="5" className="px-8 py-6">
                      <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
                        <div className="mb-4">
                          <h4 className="text-xxs font-bold text-gray-400 uppercase tracking-widest mb-2">Report Reason</h4>
                          <p className="text-sm font-medium text-gray-700 bg-gray-50 p-4 rounded-2xl">{report.reason}</p>
                        </div>
                        <div>
                          <h4 className="text-xxs font-bold text-gray-400 uppercase tracking-widest mb-2">Take Action & Respond</h4>
                          <textarea 
                            value={responseText}
                            onChange={(e) => setResponseText(e.target.value)}
                            placeholder="Type a response or internal note..."
                            className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-sm focus:ring-2 focus:ring-brand-indigo/10 outline-none mb-4 min-h-[100px]"
                          ></textarea>
                          <div className="flex items-center gap-3">
                            <button 
                              onClick={() => handleAction(report.id, 'warn')}
                              disabled={resolveMutation.isPending}
                              className="px-4 py-2 bg-yellow-50 text-yellow-600 rounded-xl font-bold text-xs hover:bg-yellow-100 transition-all flex items-center gap-2 disabled:opacity-50"
                            >
                              <Bell size={14} /> Send Warning
                            </button>
                            <button 
                              onClick={() => handleAction(report.id, 'ban')}
                              disabled={resolveMutation.isPending}
                              className="px-4 py-2 bg-red-50 text-red-600 rounded-xl font-bold text-xs hover:bg-red-100 transition-all flex items-center gap-2 disabled:opacity-50"
                            >
                              <Shield size={14} /> Take Down / Ban
                            </button>
                            <button 
                              onClick={() => handleAction(report.id, 'resolve')}
                              disabled={resolveMutation.isPending}
                              className="px-4 py-2 bg-green-50 text-green-600 rounded-xl font-bold text-xs hover:bg-green-100 transition-all flex items-center gap-2 disabled:opacity-50"
                            >
                              <Check size={14} /> Resolve & Dismiss
                            </button>
                            {resolveMutation.isPending && <Loader2 className="animate-spin text-brand-indigo" size={20} />}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminModeration;
